import React from "react"
import type { SubmitHandler } from "react-hook-form"
import { useTranslation } from "react-i18next"

import { PasswordTextField } from "@/components/form/password-text-field"
import { Form, SubmitButton, useForm } from "@/nzds"
import { minPasswordLength } from "@/schemas"

import { Box, Typography } from "@mui/material"

import type { ResetPasswordSchema } from "./reset-password-form.schema"
import { resetPasswordSchema } from "./reset-password-form.schema"
import type { ResetPasswordFormProps } from "./reset-password-form.types"

export const ResetPasswordForm: React.FC<ResetPasswordFormProps> = ({
  isSubmitting,
  onSubmit,
  user,
}) => {
  const { t } = useTranslation()
  const [isPasswordVisible, setIsPasswordVisible] = React.useState(false)

  const defaultFormValues: ResetPasswordSchema = {
    confirmPassword: "",
    password: "",
  }

  const form = useForm({
    schema: resetPasswordSchema,
    defaultValues: defaultFormValues,
  })

  const handlePasswordVisibleClick = (): void => {
    setIsPasswordVisible((isVisible) => !isVisible)
  }

  const handleSubmit: SubmitHandler<ResetPasswordSchema> = (value): void => {
    onSubmit(value)
  }

  return (
    <>
      <Typography
        sx={{
          mb: 4,
        }}
      >
        {t("resetPassword.instructions", { email: user?.email })}
      </Typography>
      <Form
        aria-labelledby="resetPasswordHeader"
        form={form}
        onSubmit={handleSubmit}
      >
        <Box
          sx={{
            mb: 1.5,
          }}
        >
          <PasswordTextField<ResetPasswordSchema>
            helperText={t("helperText.passwordRequirements", {
              min: minPasswordLength,
            })}
            id="password"
            isPasswordVisible={isPasswordVisible}
            label={t("resetPassword.newPasswordFieldLabel")}
            onPasswordVisibleClick={handlePasswordVisibleClick}
            variant="stacked"
          />
          <PasswordTextField<ResetPasswordSchema>
            id="confirmPassword"
            isPasswordVisible={isPasswordVisible}
            label={t("resetPassword.confirmNewPasswordFieldLabel")}
            onPasswordVisibleClick={handlePasswordVisibleClick}
            variant="stacked"
          />
        </Box>

        <SubmitButton fullWidth loading={isSubmitting}>
          {t("resetPassword.resetPasswordButton")}
        </SubmitButton>
      </Form>
    </>
  )
}
